import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

/**
 * ✅ INTERCEPTOR GLOBAL DE ERROS
 * 
 * Captura TODOS os erros HTTP e extrai a mensagem REAL do backend/mainframe
 * Garante que os erros não sejam mascarados e sempre mostrem o erro original
 * 
 * IMPORTANTE: Este interceptor SEMPRE propaga o erro real do mainframe/backend
 * sem tentar "traduzir" ou mascarar a mensagem.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((error: unknown) => {
      // Se não for um erro HTTP, propagar como está
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      let errorMessage = 'Erro desconhecido ao comunicar com o servidor';
      
      // 🔍 EXTRAIR MENSAGEM REAL DO BACKEND/MAINFRAME
      // Tenta várias propriedades para garantir que pegamos o erro real
      if (error.error) {
        if (typeof error.error === 'string') {
          // Backend retornou texto puro
          errorMessage = error.error;
        } else if (typeof error.error === 'object') {
          // Backend retornou JSON estruturado - tentar extrair mensagem
          errorMessage = extractErrorMessageFromObject(error.error, error.status, error.statusText);
        }
      } else if (error.message) {
        // Usar mensagem do HttpErrorResponse
        errorMessage = error.message;
      }
      
      // 🚨 IMPORTANTE: Criar um novo erro com a mensagem extraída
      // Isso garante que os componentes possam simplesmente usar error.message
      const extractedError = new Error(errorMessage);
      
      // Manter informações adicionais para debugging
      (extractedError as any).status = error.status;
      (extractedError as any).statusText = error.statusText;
      (extractedError as any).url = error.url;
      (extractedError as any).originalError = error;

      // 📋 LOG DETALHADO PARA DEBUGGING - SEMPRE mostra o erro completo no console
      console.info('❌ ERRO HTTP INTERCEPTADO:', {
        url: error.url,
        status: error.status,
        statusText: error.statusText,
        errorBody: error.error,
        extractedMessage: errorMessage,
        fullError: error
      });

      return throwError(() => extractedError);
    })
  );
};

/**
 * Extrai mensagem de erro de objeto JSON retornado pelo backend
 * Tenta várias propriedades para garantir que pegamos o erro real do mainframe
 */
function extractErrorMessageFromObject(errorObj: any, status: number, statusText: string): string {
  // 1. Tentar propriedades conhecidas do mainframe/backend
  const message = tryExtractKnownProperties(errorObj);
  if (message) {
    return message;
  }

  // 2. Tentar objeto com única propriedade string
  const singlePropertyMessage = tryExtractSingleProperty(errorObj);
  if (singlePropertyMessage) {
    return singlePropertyMessage;
  }

  // 3. Tentar serializar objeto completo
  const serializedMessage = trySerializeObject(errorObj, status, statusText);
  if (serializedMessage) {
    return serializedMessage;
  }

  // 4. Fallback com informações do status HTTP
  return `${status} ${statusText}`;
}

/**
 * Tenta extrair mensagem de propriedades conhecidas do backend/mainframe
 */
function tryExtractKnownProperties(errorObj: any): string | null {
  if (errorObj.mensagemRetorno) {
    const codigo = errorObj.codigoRetorno || errorObj.codigo || '';
    return codigo ? `[${codigo}] ${errorObj.mensagemRetorno}` : errorObj.mensagemRetorno;
  }
  
  if (errorObj.mensagem) {
    const codigo = errorObj.codigo || '';
    return codigo ? `[${codigo}] ${errorObj.mensagem}` : errorObj.mensagem;
  }
  
  const knownProps = ['error', 'message', 'erro', 'details', 'detalhes'];
  for (const prop of knownProps) {
    if (errorObj[prop]) {
      return errorObj[prop];
    }
  }
  
  return null;
}

/**
 * Tenta extrair mensagem se objeto tem apenas uma propriedade string
 */
function tryExtractSingleProperty(errorObj: any): string | null {
  const keys = Object.keys(errorObj);
  if (keys.length === 1 && typeof errorObj[keys[0]] === 'string') {
    return errorObj[keys[0]];
  }
  return null;
}

/**
 * Tenta serializar objeto completo como fallback
 */
function trySerializeObject(errorObj: any, status: number, statusText: string): string | null {
  try {
    const serialized = JSON.stringify(errorObj);
    if (serialized !== '{}' && serialized !== '{"isTrusted":true}') {
      return `${status} ${statusText}: ${serialized}`;
    }
  } catch {
    // Falha ao serializar
  }
  return null;
}
